import { useEffect, useMemo } from 'react'
import useAsync, { type AsyncResult } from '../useAsync'
import { Errors, logError } from '@/services/exceptions'
import { useCurrentChain } from '../useChains'
import { getInheritance } from '@/utils/api'
import { setInheritanceInfo, refreshInheritanceInfo, type InheritanceInfoState } from '@/store/inheritanceInfoSlice'
import useSafeAddress from '@/hooks/useSafeAddress'
import { useAppSelector, useAppDispatch, type RootState } from '@/store'

export const useLoadInheritance = (refreshCounter?: number): AsyncResult<InheritanceInfoState> => {
  const inheritanceInfo = useAppSelector((state: RootState) => state.inheritanceInfo.data)

  const dispatch = useAppDispatch()
  const safeAddress = useSafeAddress()
  const chainInfo = useCurrentChain()

  // Re-fetch assets when the entire SafeInfo updates
  const [data, error, loading] = useAsync<InheritanceInfoState>(
    () => {
      if (!chainInfo) return

      return getInheritance(chainInfo, safeAddress)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chainInfo, safeAddress, refreshCounter],
    false, // don't clear data between polls
  )

  useEffect(() => {
    if (!data) return

    dispatch(setInheritanceInfo(data))
    dispatch(refreshInheritanceInfo(true))
  }, [data, dispatch])

  // Log errors
  useEffect(() => {
    if (error) {
      logError(Errors._601, error.message)
    }
  }, [error])

  return useMemo(() => [inheritanceInfo, error, loading], [inheritanceInfo, error, loading])
}

export default useLoadInheritance
